import {useCallback, useMemo} from 'react'

import {removeAccents} from '@s-ui/js/lib/string'
import {mostSearchedMakes} from '@adv-mt/mt-shared-make-logos'

const normalizeString = str => removeAccents(str).toLowerCase().replace(/-/g, ' ')

const useOptions = ({inputSearch, options}) => {
  const mostSearchedMakesIds = useMemo(() => new Set(Object.keys(mostSearchedMakes)), [])

  const processedOptions = useMemo(() => {
    return options.map(option => ({
      ...option,
      normalizedLabel: normalizeString(option.label),
      models: option.models.map(model => ({
        ...model,
        normalizedLabel: normalizeString(model.label)
      }))
    }))
  }, [options])

  const checkBrandMatch = useCallback((normalizedLabel, searchTerms) => {
    const brandWords = normalizedLabel.split(' ')
    const [mainSearchTerm, ...additionalSearchTerms] = searchTerms

    const fullMatch = normalizedLabel === searchTerms.join(' ')
    const partialMatch =
      normalizedLabel.startsWith(mainSearchTerm) ||
      (searchTerms.length > 1 && searchTerms.every((term, index) => brandWords[index]?.startsWith(term)))

    return {fullMatch, partialMatch, additionalSearchTerms}
  }, [])

  const filterModels = useCallback((models, searchTerms, brandFullMatch, brandPartialMatch, additionalSearchTerms) => {
    if (brandFullMatch) return models
    if (brandPartialMatch && additionalSearchTerms.length === 0) return models

    return models.filter(({normalizedLabel: modelNormalizedLabel}) => {
      if (brandPartialMatch) {
        return additionalSearchTerms.every(term => modelNormalizedLabel.includes(term))
      }
      return searchTerms.every(term => modelNormalizedLabel.includes(term))
    })
  }, [])

  const searchResults = useMemo(() => {
    const normalizedSearch = normalizeString(inputSearch.trim())
    const searchTerms = normalizedSearch.split(/\s+/)

    const mostSearchedMakesGroup = new Set()
    const restOfMakesGroup = new Set()

    if (!normalizedSearch) {
      processedOptions.forEach(({allowsAllModels = true, id, label, models}) => {
        const group = mostSearchedMakesIds.has(String(id)) ? mostSearchedMakesGroup : restOfMakesGroup
        group.add({hasAllSelectionsLabel: allowsAllModels, id, isOpened: false, label, models})
      })
    } else {
      processedOptions.forEach(({allowsAllModels = true, id, label, normalizedLabel, models}) => {
        const group = mostSearchedMakesIds.has(String(id)) ? mostSearchedMakesGroup : restOfMakesGroup

        const {fullMatch, partialMatch, additionalSearchTerms} = checkBrandMatch(normalizedLabel, searchTerms)
        const filteredModels = filterModels(models, searchTerms, fullMatch, partialMatch, additionalSearchTerms)

        if (fullMatch || partialMatch || filteredModels.length > 0) {
          group.add({
            hasAllSelectionsLabel:
              allowsAllModels && (fullMatch || (partialMatch && additionalSearchTerms.length === 0)),
            id,
            isOpened: true,
            label,
            models: filteredModels
          })
        }
      })
    }

    return {mostSearchedMakesGroup, restOfMakesGroup}
  }, [inputSearch, processedOptions, mostSearchedMakesIds, checkBrandMatch, filterModels])

  return searchResults
}

export default useOptions
